import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/auth-context";
import tokenService from "../../services/token-service";
import { useNavigate } from "react-router-dom";
import CameraCapture from "../../components/common/camera-capture";

const workingDayOptions = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const HandymanOnboarding = () => {
  const { isAuthenticated, refreshUser } = useAuth();
  const [loggedInUser, setLoggedInUser] = useState(() =>
    tokenService.getUserDetails(),
  );
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cnicPreviews, setCnicPreviews] = useState({ front: null, back: null });
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [mainServices, setMainServices] = useState([]);
  const [mainServicesLoading, setMainServicesLoading] = useState(false);
  const [mainServiceError, setMainServiceError] = useState(null);
  const [subServices, setSubServices] = useState([]);
  const [subServicesLoading, setSubServicesLoading] = useState(false);
  const [subServiceError, setSubServiceError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReapplication, setIsReapplication] = useState(false);
  const navigate = useNavigate();

  const [subServiceSearch, setSubServiceSearch] = useState("");
  const [subServiceView, setSubServiceView] = useState("all");
  const [visibleSubServiceCount, setVisibleSubServiceCount] = useState(12);

  const [formValues, setFormValues] = useState({
    userId: loggedInUser?._id || "",
    mainService: "",
    subServices: [],
    profileImage: null,
    cnicNumber: "",
    residentialAddress: loggedInUser?.residentialAddress || "",
    cnicFrontImage: null,
    cnicBackImage: null,
    certifications: [],
    availability: {
      isAvailable: true,
      workingHours: { start: "", end: "" },
      workingDays: [],
    },
    bankDetails: {
      accountTitle: "",
      accountNumber: "",
      bankName: "",
      branchCode: "",
    },
    serviceAreas: [],
  });

  useEffect(() => {
    setLoggedInUser(tokenService.getUserDetails());
  }, [isAuthenticated]);

  useEffect(() => {
    setFormValues((prev) => ({
      ...prev,
      userId: loggedInUser?._id || "",
      residentialAddress: loggedInUser?.residentialAddress || "",
    }));
  }, [loggedInUser]);

  useEffect(() => {
    const fetchMainServices = async () => {
      try {
        setMainServicesLoading(true);
        setMainServiceError(null);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/services/categories`,
        );
        const result = await response.json();
        if (result.success && result.data) {
          setMainServices(result.data);
        } else {
          setMainServiceError(
            result.message || "Failed to load main services.",
          );
        }
      } catch (error) {
        setMainServiceError("An error occurred while fetching main services.");
        console.error("Error fetching main services:", error);
      } finally {
        setMainServicesLoading(false);
      }
    };
    fetchMainServices();
  }, []);

  useEffect(() => {
    const fetchExistingProfile = async () => {
      if (!loggedInUser?._id) return;
      try {
        const token = tokenService.getToken();
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/handyman/profile/${loggedInUser._id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await res.json();
        if (result.success && result.data?.handyman) {
          const hm = result.data.handyman;
          
          if (hm.verification?.status === "Rejected") {
            setIsReapplication(true);
            
            const targetMainService = hm.mainService?._id || hm.mainService || "";
            
            if (targetMainService) {
              try {
                setSubServicesLoading(true);
                const subRes = await fetch(
                  `${process.env.REACT_APP_API_URL}/services/sub-categories/by-main/${targetMainService}`,
                );
                const subResult = await subRes.json();
                if (subResult.success && subResult.data) {
                  setSubServices(subResult.data);
                }
              } catch (error) {
                console.error("Error fetching sub services:", error);
              } finally {
                setSubServicesLoading(false);
              }
            }

            setFormValues((prev) => ({
              ...prev,
              mainService: targetMainService,
              subServices: hm.subServices?.map((s) => s._id || s) || [],
              cnicNumber: hm.cnicNumber || "",
              residentialAddress: hm.residentialAddress || "",
              availability: hm.availability || prev.availability,
              bankDetails: hm.bankDetails || prev.bankDetails,
              serviceAreas: hm.serviceAreas || [],
            }));

            if (hm.profileImage) {
              setPreviewUrl(hm.profileImage);
            }
            
            const docs = result.data.documents || [];
            const cnicFrontDoc = docs.find(d => d.documentType === "CNIC Front");
            const cnicBackDoc = docs.find(d => d.documentType === "CNIC Back");
            
            setCnicPreviews({
              front: cnicFrontDoc?.filePath || null,
              back: cnicBackDoc?.filePath || null,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching existing handyman profile:", err);
      }
    };

    fetchExistingProfile();
  }, [loggedInUser]);

  const handleMainServiceChange = async (mainServiceId) => {
    setFormValues((prev) => ({
      ...prev,
      mainService: mainServiceId,
      subServices: [],
    }));
    setSubServices([]);
    setSubServiceError(null);
    setErrors((prev) => ({ ...prev, mainService: "", subServices: "" }));
    setSubServiceSearch("");
    setSubServiceView("all");
    setVisibleSubServiceCount(12);

    if (!mainServiceId) return;

    try {
      setSubServicesLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/services/sub-categories/by-main/${mainServiceId}`,
      );
      const result = await response.json();
      if (result.success && result.data) {
        setSubServices(result.data);
      } else {
        setSubServiceError(result.message || "Failed to load sub services.");
      }
    } catch (error) {
      setSubServiceError("An error occurred while fetching sub services.");
      console.error("Error fetching sub services:", error);
    } finally {
      setSubServicesLoading(false);
    }
  };
  const formatCNIC = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 5) {
      return digits;
    } else if (digits.length <= 12) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    } else {
      return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12, 13)}`;
    }
  };

  const validateCNIC = (cnic) => {
    const cnicRegex = /^\d{5}-\d{7}-\d{1}$/;
    return cnicRegex.test(cnic);
  };

  const handleCNICChange = (e) => {
    const rawValue = e.target.value;
    const formatted = formatCNIC(rawValue);

    setFormValues((prev) => ({
      ...prev,
      cnicNumber: formatted,
    }));

    if (validateCNIC(formatted)) {
      setErrors((prev) => ({ ...prev, cnicNumber: "" }));
    }
  };
  const validateForm = () => {
    console.log("DEBUG: validateForm triggered. formValues:", formValues);
    const newErrors = {};

    if (!formValues.mainService) {
      newErrors.mainService = "Main service is required";
    }
    if (formValues.subServices.length === 0) {
      newErrors.subServices = "Select at least one sub service";
    }

    if (!formValues.cnicNumber) {
      newErrors.cnicNumber = "CNIC number is required";
    } else if (!validateCNIC(formValues.cnicNumber)) {
      newErrors.cnicNumber = "Enter a valid CNIC in format: XXXXX-XXXXXXX-X";
    }
    if (
      !formValues.residentialAddress ||
      !formValues.residentialAddress.trim()
    ) {
      newErrors.residentialAddress = "Residential address is required";
    }
    if (!isReapplication && !formValues.profileImage) {
      newErrors.profileImage = "Profile image is required";
    }
    if (!isReapplication && !formValues.cnicFrontImage) {
      newErrors.cnicFrontImage = "CNIC front image is required";
    }
    if (!isReapplication && !formValues.cnicBackImage) {
      newErrors.cnicBackImage = "CNIC back image is required";
    }

    if (formValues.certifications.length > 3) {
      newErrors.certifications = "You can upload up to 3 certifications.";
    }

    console.log("DEBUG: validateForm completed. errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildOnboardingPayload = () => {
    const formData = new FormData();

    formData.append("userId", formValues.userId);
    formData.append("mainServiceId", formValues.mainService);
    formData.append("subServices", JSON.stringify(formValues.subServices));

    formData.append("cnicNumber", formValues.cnicNumber);
    formData.append("residentialAddress", formValues.residentialAddress);

    if (formValues.profileImage) {
      formData.append("profileImage", formValues.profileImage);
    }

    if (formValues.cnicFrontImage) {
      formData.append("cnicFrontImage", formValues.cnicFrontImage);
    }
    if (formValues.cnicBackImage) {
      formData.append("cnicBackImage", formValues.cnicBackImage);
    }

    if (formValues.certifications.length > 0) {
      formValues.certifications.forEach((file) => {
        formData.append("certifications", file);
      });
    }

    formData.append("availability", JSON.stringify(formValues.availability));
    formData.append("bankDetails", JSON.stringify(formValues.bankDetails));

    if (formValues.serviceAreas && formValues.serviceAreas.length > 0) {
      formData.append("serviceAreas", JSON.stringify(formValues.serviceAreas));
    }

    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("DEBUG: handleSubmit triggered.");

    if (!formValues.userId) {
      console.log("DEBUG: missing userId in formValues:", formValues);
      toast.error(
        "User information is missing. Please log in before submitting.",
      );
      return;
    }

    if (!validateForm()) {
      console.log("DEBUG: form validation failed.");
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    console.log("DEBUG: Form validated successfully. Submitting to API...");
    setIsSubmitting(true);

    try {
      const payload = buildOnboardingPayload();

      const token = tokenService.getToken();
      console.log("DEBUG: Auth token retrieved:", token ? "YES (present)" : "NO (missing)");
      const apiUrl = `${process.env.REACT_APP_API_URL}/handyman/onboarding`;
      console.log("DEBUG: Requesting API URL:", apiUrl);

      const response = await fetch(
        apiUrl,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: payload,
        },
      );

      const result = await response.json().catch(() => null);

      if (!response.ok || result?.error) {
        const errorData = result?.error || result;
        const errorMessage =
          errorData?.message ||
          result?.message ||
          "Failed to submit onboarding details.";
        const errorCode = errorData?.code || result?.code;

        if (
          errorCode === "ALREADY_ONBOARDED" ||
          errorCode === "DUPLICATE_PROFILE"
        ) {
          toast.error("You have already completed onboarding.");
        } else if (errorCode === "VALIDATION_ERROR") {
          toast.error(errorMessage);
        } else if (errorCode === "USER_NOT_FOUND") {
          toast.error("User account not found. Please log in again.");
        } else if (errorCode === "INVALID_ROLE") {
          toast.error("Only handyman users can complete onboarding.");
        } else {
          toast.error(errorMessage);
        }

        return;
      }

      toast.success(
        result?.message || "Handyman onboarding completed successfully!",
      );
      if (refreshUser) {
        await refreshUser();
      }
      navigate("/handyman/dashboard");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error?.message || "Something went wrong while submitting.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSubService = (subServiceId) => {
    setFormValues((prev) => {
      const currentSubServices = prev.subServices || [];
      if (currentSubServices.includes(subServiceId)) {
        return {
          ...prev,
          subServices: currentSubServices.filter((id) => id !== subServiceId),
        };
      } else {
        return {
          ...prev,
          subServices: [...currentSubServices, subServiceId],
        };
      }
    });
    setErrors((prev) => ({ ...prev, subServices: "" }));
  };

  const selectAllSubServices = () => {
    if (!subServices.length) return;
    setFormValues((prev) => ({
      ...prev,
      subServices: subServices.map((s) => s._id || s.id),
    }));
    setErrors((prev) => ({ ...prev, subServices: "" }));
  };

  const selectAllFilteredSubServices = (filtered) => {
    if (!filtered.length) return;
    setFormValues((prev) => {
      const ids = filtered.map((s) => s._id || s.id);

      const merged = Array.from(new Set([...(prev.subServices || []), ...ids]));
      return { ...prev, subServices: merged };
    });
    setErrors((prev) => ({ ...prev, subServices: "" }));
  };

  const resetSubServices = () => {
    setFormValues((prev) => ({
      ...prev,
      subServices: [],
    }));
  };

  const toggleWorkingDay = (day) => {
    setFormValues((prev) => {
      const currentDays = prev.availability.workingDays || [];
      if (currentDays.includes(day)) {
        return {
          ...prev,
          availability: {
            ...prev.availability,
            workingDays: currentDays.filter((item) => item !== day),
          },
        };
      } else {
        return {
          ...prev,
          availability: {
            ...prev.availability,
            workingDays: [...currentDays, day],
          },
        };
      }
    });
  };

  const handleCnicUpload = (event, position) => {
    const file = event.target.files?.[0] || null;
    const fieldName = position === "front" ? "cnicFrontImage" : "cnicBackImage";

    if (file) {
      const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/bmp", "image/gif"];
      const fileExt = file.name.split('.').pop().toLowerCase();
      const allowedExts = ["jpg", "jpeg", "png", "webp", "bmp", "gif"];
      if (!allowedImageTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: "Only image files (JPG, JPEG, PNG, WEBP) are allowed for CNIC.",
        }));
        event.target.value = "";
        setCnicPreviews((prev) => {
          if (prev[position]) URL.revokeObjectURL(prev[position]);
          return {
            ...prev,
            [position]: null,
          };
        });
        return;
      }
    }

    setFormValues((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    setCnicPreviews((prev) => {
      if (prev[position]) URL.revokeObjectURL(prev[position]);
      return {
        ...prev,
        [position]: file ? URL.createObjectURL(file) : null,
      };
    });

    setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const handleCertificationUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const currentCount = formValues.certifications.length;
    const availableSlots = 3 - currentCount;

    if (availableSlots <= 0) {
      setErrors((prev) => ({
        ...prev,
        certifications: "You can upload up to 3 certifications.",
      }));
      event.target.value = "";
      return;
    }

    const filesToAdd = files.slice(0, availableSlots);

    setFormValues((prev) => ({
      ...prev,
      certifications: [...prev.certifications, ...filesToAdd],
    }));

    setErrors((prev) => ({
      ...prev,
      certifications:
        files.length > availableSlots
          ? "You can upload up to 3 certifications."
          : "",
    }));

    event.target.value = "";
  };

  const removeCertification = (index) => {
    setFormValues((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, idx) => idx !== index),
    }));
  };

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const handleCameraCapture = (file) => {
    handleInputChange("profileImage", file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setShowCameraModal(false);
    toast.success("Photo captured successfully!");
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (cnicPreviews.front) URL.revokeObjectURL(cnicPreviews.front);
      if (cnicPreviews.back) URL.revokeObjectURL(cnicPreviews.back);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSubServiceSet = useMemo(() => {
    return new Set(formValues.subServices || []);
  }, [formValues.subServices]);

  const filteredSubServices = useMemo(() => {
    const q = subServiceSearch.trim().toLowerCase();
    const base = Array.isArray(subServices) ? subServices : [];

    let list = base;

    if (q) {
      list = list.filter((s) => {
        const name = (s?.name || "").toLowerCase();
        const desc = (s?.description || "").toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }

    if (subServiceView === "selected") {
      list = list.filter((s) => selectedSubServiceSet.has(s._id || s.id));
    }

    list = [...list].sort((a, b) => {
      const aSel = selectedSubServiceSet.has(a._id || a.id) ? 1 : 0;
      const bSel = selectedSubServiceSet.has(b._id || b.id) ? 1 : 0;
      return bSel - aSel;
    });

    return list;
  }, [subServices, subServiceSearch, subServiceView, selectedSubServiceSet]);

  const visibleSubServices = useMemo(() => {
    return filteredSubServices.slice(0, visibleSubServiceCount);
  }, [filteredSubServices, visibleSubServiceCount]);

  const totalSelectedCount = formValues.subServices?.length || 0;

  const removeSelectedChip = (id) => {
    toggleSubService(id);
  };

  const getSelectedSubServiceObjects = useMemo(() => {
    if (!subServices?.length || !formValues.subServices?.length) return [];
    const idSet = new Set(formValues.subServices);
    return subServices.filter((s) => idSet.has(s._id || s.id)).slice(0, 12);
  }, [subServices, formValues.subServices]);

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-amber-50 via-white to-amber-100">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 px-4 py-8 md:px-8 lg:px-10 lg:py-10">
        <div className="grid h-full items-stretch gap-8 lg:grid-cols-[1.2fr,2fr]">
          <section className="flex h-full flex-col justify-between gap-6 rounded-3xl bg-gradient-to-br from-orange-500 via-orange-400 to-orange-300 p-10 text-white shadow-2xl">
            <div className="space-y-4">
              <p className="inline-flex items-center rounded-full bg-white/20 px-4 py-1 text-sm font-medium uppercase tracking-wide">
                Handyman onboarding
              </p>
              <h1 className="text-3xl font-bold leading-tight md:text-4xl">
                Tell us about your expertise so we can match you with the
                perfect jobs.
              </h1>
              <p className="text-base text-white/85 md:text-lg">
                Share the services you provide, when you like to work, and the
                areas you cover.
              </p>
            </div>
            <div className="space-y-4 rounded-2xl bg-white/10 p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80">Need help?</p>
                  <p className="text-lg font-semibold">
                    support@hazerglobal.com
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="flex h-full flex-col overflow-hidden rounded-3xl border border-white/60 bg-white/90 shadow-xl">
            <div className="border-b border-amber-200 bg-amber-50 px-8 py-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {isReapplication ? "Update and resubmit your profile (Appeal)" : "Create your professional profile"}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {isReapplication 
                  ? "Please review and update any details as required before resubmitting your application."
                  : "We keep your details secure and only share what's needed with customers."}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8">
              <form onSubmit={handleSubmit} className="space-y-12">
                <section className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Service focus
                    </h3>
                    <p className="text-sm text-gray-600">
                      Choose your primary trade and the specific services you
                      deliver.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        Primary service
                      </h4>
                      {mainServicesLoading && (
                        <span className="text-xs font-medium uppercase tracking-wide text-orange-500">
                          Loading...
                        </span>
                      )}
                    </div>

                    {mainServiceError && (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                        {mainServiceError}
                      </div>
                    )}

                    {!mainServicesLoading && mainServices.length > 0 && (
                      <select
                        value={formValues.mainService}
                        onChange={(e) =>
                          handleMainServiceChange(e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                      >
                        <option value="">Select a main service</option>
                        {mainServices.map((service) => (
                          <option key={service._id} value={service._id}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                    )}

                    {errors.mainService && (
                      <p className="text-xs text-red-500">
                        {errors.mainService}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">
                            Sub services
                          </h4>
                          <p className="text-xs text-gray-500">
                            Search, select, and manage many services without
                            endless scrolling.
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="hidden sm:inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-orange-700">
                            Selected: {totalSelectedCount}
                          </span>
                        </div>
                      </div>

                      <div className="sticky top-0 z-10 rounded-2xl border border-amber-200 bg-white/95 p-3 shadow-sm backdrop-blur">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="relative flex-1">
                            <input
                              value={subServiceSearch}
                              onChange={(e) => {
                                setSubServiceSearch(e.target.value);
                                setVisibleSubServiceCount(12);
                              }}
                              placeholder="Search sub services (e.g., wiring, fan, switchboard)"
                              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                            />
                            {subServiceSearch && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSubServiceSearch("");
                                  setVisibleSubServiceCount(12);
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200"
                              >
                                Clear
                              </button>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <div className="inline-flex rounded-full border border-gray-200 bg-white p-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setSubServiceView("all");
                                  setVisibleSubServiceCount(12);
                                }}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                                  subServiceView === "all"
                                    ? "bg-orange-500 text-white"
                                    : "text-gray-600 hover:text-orange-600"
                                }`}
                              >
                                All
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSubServiceView("selected");
                                  setVisibleSubServiceCount(12);
                                }}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                                  subServiceView === "selected"
                                    ? "bg-orange-500 text-white"
                                    : "text-gray-600 hover:text-orange-600"
                                }`}
                              >
                                Selected ({totalSelectedCount})
                              </button>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                if (subServiceSearch.trim()) {
                                  selectAllFilteredSubServices(
                                    filteredSubServices,
                                  );
                                } else {
                                  selectAllSubServices();
                                }
                              }}
                              disabled={!subServices.length}
                              className="rounded-full border border-orange-500 px-3 py-1 text-xs font-semibold text-orange-600 transition hover:bg-orange-500 hover:text-white disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
                              title={
                                subServiceSearch.trim()
                                  ? "Select all matched results"
                                  : "Select all sub services"
                              }
                            >
                              Select all
                            </button>

                            <button
                              type="button"
                              onClick={resetSubServices}
                              disabled={!formValues.subServices.length}
                              className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-orange-500 hover:text-orange-600 disabled:cursor-not-allowed disabled:text-gray-300"
                            >
                              Reset
                            </button>
                          </div>
                        </div>

                        {totalSelectedCount > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {getSelectedSubServiceObjects.map((s) => {
                              const id = s._id || s.id;
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => removeSelectedChip(id)}
                                  className="group inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-orange-700 hover:border-orange-300 hover:bg-amber-100"
                                  title="Click to remove"
                                >
                                  <span className="truncate max-w-[180px]">
                                    {s.name}
                                  </span>
                                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-gray-600 group-hover:text-gray-800">
                                    ✕
                                  </span>
                                </button>
                              );
                            })}
                            {totalSelectedCount > 12 && (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                                +{totalSelectedCount - 12} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {subServicesLoading ? (
                        <div className="rounded-xl border border-dashed border-orange-400 bg-amber-50 px-4 py-6 text-center text-sm font-medium text-orange-500">
                          Fetching sub services...
                        </div>
                      ) : filteredSubServices.length ? (
                        <div className="rounded-2xl border border-gray-200 bg-white">
                          <div className="max-h-[420px] overflow-auto p-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                              {visibleSubServices.map((subService) => {
                                const serviceId =
                                  subService._id || subService.id;
                                const isChecked =
                                  formValues.subServices.includes(serviceId);

                                return (
                                  <label
                                    key={serviceId}
                                    className={`relative flex h-full cursor-pointer flex-col rounded-xl border px-4 py-4 transition ${
                                      isChecked
                                        ? "border-orange-200 bg-amber-50 shadow-sm"
                                        : "border-gray-200 bg-white hover:border-orange-400"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={isChecked}
                                      onChange={() =>
                                        toggleSubService(serviceId)
                                      }
                                    />

                                    <div className="flex items-start justify-between gap-3">
                                      <span className="text-sm font-semibold text-gray-900">
                                        {subService.name}
                                      </span>

                                      <span
                                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold ${
                                          isChecked
                                            ? "border-orange-500 bg-orange-500 text-white"
                                            : "border-gray-200 text-gray-300"
                                        }`}
                                      >
                                        ✓
                                      </span>
                                    </div>

                                    {subService.description && (
                                      <span className="mt-2 text-xs text-gray-500 line-clamp-2">
                                        {subService.description}
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-gray-600">
                              Showing{" "}
                              <span className="font-semibold text-gray-900">
                                {Math.min(
                                  visibleSubServiceCount,
                                  filteredSubServices.length,
                                )}
                              </span>{" "}
                              of{" "}
                              <span className="font-semibold text-gray-900">
                                {filteredSubServices.length}
                              </span>{" "}
                              {subServiceView === "selected"
                                ? "selected"
                                : "results"}
                            </p>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setVisibleSubServiceCount((c) =>
                                    Math.max(12, c - 12),
                                  )
                                }
                                disabled={visibleSubServiceCount <= 12}
                                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-600 hover:border-orange-400 hover:text-orange-600 disabled:cursor-not-allowed disabled:text-gray-300"
                              >
                                Show less
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  setVisibleSubServiceCount((c) => c + 12)
                                }
                                disabled={
                                  visibleSubServiceCount >=
                                  filteredSubServices.length
                                }
                                className="rounded-full border border-orange-500 bg-white px-3 py-1 text-xs font-semibold text-orange-600 hover:bg-orange-500 hover:text-white disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
                              >
                                Load more
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                          {formValues.mainService
                            ? subServiceSearch.trim()
                              ? "No matches found. Try a different keyword."
                              : subServiceView === "selected"
                                ? "No selected sub services yet."
                                : "No sub services available"
                            : "Select a main service to view sub services"}
                        </div>
                      )}

                      {errors.subServices && (
                        <p className="text-xs text-red-500">
                          {errors.subServices}
                        </p>
                      )}
                      {subServiceError && (
                        <p className="text-xs text-red-500">
                          {subServiceError}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Contact details
                    </h3>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
                        CNIC Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formValues.cnicNumber}
                        onChange={handleCNICChange}
                        placeholder="XXXXX-XXXXXXX-X"
                        maxLength={15}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                      />
                      {errors.cnicNumber && (
                        <p className="text-xs text-red-500">
                          {errors.cnicNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
                      Residential Address{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formValues.residentialAddress}
                      onChange={(e) =>
                        handleInputChange("residentialAddress", e.target.value)
                      }
                      rows={3}
                      placeholder="House #, Street, City"
                      className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                    />
                    {errors.residentialAddress && (
                      <p className="text-xs text-red-500">
                        {errors.residentialAddress}
                      </p>
                    )}
                  </div>
                </section>

                <section className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Availability
                    </h3>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white/50 p-6">
                    <div className="space-y-4">
                      <p className="text-sm font-medium text-gray-800">
                        Working days
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {workingDayOptions.map((day) => {
                          const isActive =
                            formValues.availability.workingDays.includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleWorkingDay(day)}
                              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                                isActive
                                  ? "border-transparent bg-orange-500 text-white"
                                  : "border-gray-200 text-gray-600 hover:border-orange-500"
                              }`}
                            >
                              {day.slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>

                      <div className="grid gap-4 pt-4 md:grid-cols-2">
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={formValues.availability.workingHours.start}
                            onChange={(e) =>
                              setFormValues((prev) => ({
                                ...prev,
                                availability: {
                                  ...prev.availability,
                                  workingHours: {
                                    ...prev.availability.workingHours,
                                    start: e.target.value,
                                  },
                                },
                              }))
                            }
                            className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={formValues.availability.workingHours.end}
                            onChange={(e) =>
                              setFormValues((prev) => ({
                                ...prev,
                                availability: {
                                  ...prev.availability,
                                  workingHours: {
                                    ...prev.availability.workingHours,
                                    end: e.target.value,
                                  },
                                },
                              }))
                            }
                            className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Documents
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      All documents will be reviewed by admin before approval
                    </p>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="flex flex-col gap-3">
                      <label className="text-xs font-medium uppercase text-gray-600">
                        CNIC Front <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleCnicUpload(e, "front")}
                        className="rounded-xl border border-dashed border-orange-400 bg-amber-50 px-4 py-3 text-sm file:mr-4 file:rounded-full file:border-0 file:bg-orange-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-600"
                      />
                      {errors.cnicFrontImage && (
                        <p className="text-xs text-red-500">
                          {errors.cnicFrontImage}
                        </p>
                      )}
                      {cnicPreviews.front && (
                        <img
                          src={cnicPreviews.front}
                          alt="CNIC front"
                          className="h-24 w-full rounded-lg object-cover shadow"
                        />
                      )}
                    </div>

                    <div className="flex flex-col gap-3">
                      <label className="text-xs font-medium uppercase text-gray-600">
                        CNIC Back <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleCnicUpload(e, "back")}
                        className="rounded-xl border border-dashed border-orange-400 bg-amber-50 px-4 py-3 text-sm file:mr-4 file:rounded-full file:border-0 file:bg-orange-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-600"
                      />
                      {errors.cnicBackImage && (
                        <p className="text-xs text-red-500">
                          {errors.cnicBackImage}
                        </p>
                      )}
                      {cnicPreviews.back && (
                        <img
                          src={cnicPreviews.back}
                          alt="CNIC back"
                          className="h-24 w-full rounded-lg object-cover shadow"
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="text-xs font-medium uppercase text-gray-600">
                      Profile Image <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCameraModal(true)}
                      className="rounded-xl border-2 border-dashed border-orange-400 bg-gradient-to-br from-amber-50 to-orange-50 px-6 py-8 text-sm font-semibold text-orange-600 hover:from-amber-100 hover:to-orange-100 transition-all duration-200 flex items-center justify-center gap-3 group"
                    >
                      <span className="text-3xl group-hover:scale-110 transition-transform">
                        📷
                      </span>
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">
                          Capture Your Photo
                        </p>
                        <p className="text-xs text-gray-600 font-normal">
                          Use your device camera
                        </p>
                      </div>
                    </button>
                    {errors.profileImage && (
                      <p className="text-xs text-red-500">
                        {errors.profileImage}
                      </p>
                    )}
                    {previewUrl && (
                      <div className="flex justify-center">
                        <div className="relative inline-block group">
                          <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-orange-200 bg-white p-1">
                            <img
                              src={previewUrl}
                              alt="Profile"
                              className="h-40 w-40 object-cover rounded-xl"
                            />
                          </div>

                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-16 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            <div className="bg-gray-900 text-white text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
                              Photo Ready ✓
                            </div>
                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-b-gray-900"></div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setPreviewUrl(null);
                              handleInputChange("profileImage", null);
                            }}
                            className="absolute -top-3 -left-3 rounded-full bg-red-500 hover:bg-red-600 text-white p-2 transition shadow-lg border-4 border-white hover:scale-110"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="text-xs font-medium uppercase text-gray-600">
                      Certifications{" "}
                      <span className="text-orange-500">(Recommended)</span>
                    </label>
                    <p className="text-xs text-gray-500">
                      You can upload up to 3 certifications.
                    </p>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleCertificationUpload}
                      className="rounded-xl border border-dashed border-orange-400 bg-amber-50 px-4 py-4 text-sm file:mr-4 file:rounded-full file:border-0 file:bg-orange-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-600"
                    />
                    {errors.certifications && (
                      <p className="text-xs text-orange-500">
                        {errors.certifications}
                      </p>
                    )}
                    {formValues.certifications.length > 0 && (
                      <ul className="space-y-2">
                        {formValues.certifications.map((file, index) => (
                          <li
                            key={index}
                            className="flex items-center justify-between rounded-xl border border-gray-100 bg-amber-50 px-3 py-2 text-sm"
                          >
                            <span className="truncate flex-1">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeCertification(index)}
                              className="ml-2 text-xs font-semibold text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>

                <section className="space-y-6 opacity-60">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Payment details
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Your payment information is secure and encrypted
                    </p>
                  </div>

                  <fieldset disabled className="grid gap-4 md:grid-cols-2">
                    <input
                      type="text"
                      value={formValues.bankDetails.accountTitle}
                      onChange={(e) =>
                        handleNestedInputChange(
                          "bankDetails",
                          "accountTitle",
                          e.target.value,
                        )
                      }
                      placeholder="Account title"
                      className="cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                    />
                    <input
                      type="text"
                      value={formValues.bankDetails.accountNumber}
                      onChange={(e) =>
                        handleNestedInputChange(
                          "bankDetails",
                          "accountNumber",
                          e.target.value,
                        )
                      }
                      placeholder="Account number"
                      className="cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                    />
                    <input
                      type="text"
                      value={formValues.bankDetails.bankName}
                      onChange={(e) =>
                        handleNestedInputChange(
                          "bankDetails",
                          "bankName",
                          e.target.value,
                        )
                      }
                      placeholder="Bank name"
                      className="cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                    />
                    <input
                      type="text"
                      value={formValues.bankDetails.branchCode}
                      onChange={(e) =>
                        handleNestedInputChange(
                          "bankDetails",
                          "branchCode",
                          e.target.value,
                        )
                      }
                      placeholder="Branch code"
                      className="cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500"
                    />
                  </fieldset>
                </section>

                <div className="flex justify-end gap-3 border-t pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-full bg-orange-500 px-8 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? "Submitting..." : "Submit onboarding"}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      </div>

      {showCameraModal && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCameraModal(false)}
          facingMode="user"
        />
      )}
    </div>
  );
};

export default HandymanOnboarding;
